import type { Card } from '../trick/cards.js';
import { sameCard } from '../trick/cards.js';
import { trickWinner } from '../trick/rules.js';
import { partnerOf } from '../bridge/seats.js';
import type { WhistView } from './view.js';

/** `easy` plays any legal card. `clever` plays by simple rules of thumb. */
export type WhistRobotLevel = 'easy' | 'clever';

const lowest = (cards: readonly Card[]): Card => cards.reduce((a, b) => (b.rank < a.rank ? b : a));

/**
 * The card a robot plays; it sees exactly what a human in its seat sees (a `WhistView`). `random` is
 * used by the easy robot only, so a clever game is fully deterministic.
 */
export function whistChooseCard(view: WhistView, level: WhistRobotLevel, random: () => number): Card {
  const legal = view.legalPlays;
  if (legal.length === 0) throw new Error("whistChooseCard: nothing to play (not this seat's turn?)");
  if (legal.length === 1) return legal[0] as Card;
  if (level === 'easy') return legal[Math.floor(random() * legal.length)] as Card;

  let card: Card;
  if (view.trick.length === 0) {
    // Lead: an Ace if we hold one (a sure trick outside trump is likely), else the lowest card of our longest suit.
    const aces = legal.filter((c) => c.rank === 14 && c.suit !== view.trump);
    if (aces.length > 0) card = aces[0] as Card;
    else {
      const count = (s: string) => legal.filter((c) => c.suit === s).length;
      const longest = legal.reduce((a, b) => (count(b.suit) > count(a.suit) ? b : a)).suit;
      card = lowest(legal.filter((c) => c.suit === longest));
    }
  } else {
    const winner = trickWinner(view.trick, view.trump);
    const partnerWinning = winner === partnerOf(view.viewer);
    if (partnerWinning) card = lowest(legal);
    else {
      // Win the trick as cheaply as we can; if we cannot, give up the lowest card.
      const winning = legal.filter((c) => trickWinner([...view.trick, { seat: view.viewer, card: c }], view.trump) === view.viewer);
      card = winning.length > 0 ? lowest(winning) : lowest(legal);
    }
  }
  return legal.find((c) => sameCard(c, card)) ?? (legal[0] as Card);
}
